import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ThirdPartyLogo from '../components/ThirdPartyLogo';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function DisclosurePortal() {
  const { token } = useParams();
  const [disclosure, setDisclosure] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [file, setFile]             = useState(null);
  const [uploading, setUploading]   = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [done, setDone]             = useState(false);

  useEffect(() => {
    fetch(`${API}/api/disclosure/${token}`)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setDisclosure(d.disclosure); setLoading(false); })
      .catch(() => { setError('Could not load this page.'); setLoading(false); });
  }, [token]);

  const handleSubmit = async () => {
    if (!file) { setUploadError('Please select a file.'); return; }
    setUploading(true);
    setUploadError(null);
    try {
      // Read file as base64
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await fetch(`${API}/api/disclosure/${token}/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdf_data: base64, pdf_filename: file.name }),
      });
      // The virus check's own sentence when it refuses the file (422 { message }); anything else stays generic.
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(res.status === 422 && j.message ? j.message : 'Upload failed');
      }
      setDone(true);
    } catch (e) {
      setUploadError(e.message || 'Upload failed. Please try again.');
    } finally { setUploading(false); }
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#9ca3af' }}>Loading...</div>;
  if (error) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#ff082f', textAlign: 'center', padding: '20px' }}>{error}</div>;

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '20px', maxWidth: '500px', width: '100%', overflow: 'hidden', boxShadow: '0 4px 32px rgba(0,0,0,0.10)' }}>
        <ThirdPartyLogo band />
        <div style={{ background: '#3a6ad9', padding: '14px 36px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Seller's Disclosure Upload</div>
        </div>
        <div style={{ padding: '32px 36px' }}>
          {done ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '800', color: '#111827' }}>Disclosure Submitted</h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', lineHeight: '1.7' }}>
                Thank you! We received the Seller's Disclosure for <strong>{disclosure?.property_address}</strong>. Our team will review it shortly.
              </p>
            </div>
          ) : disclosure?.status === 'received' ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '800', color: '#111827' }}>Already Submitted</h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                The disclosure for <strong>{disclosure?.property_address}</strong> has already been uploaded. Contact support if you need to update it.
              </p>
            </div>
          ) : (
            <>
              <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '800', color: '#111827' }}>Upload Seller's Disclosure</h2>
              <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#6b7280', lineHeight: '1.7' }}>
                Please upload the completed Seller's Disclosure for <strong>{disclosure?.property_address || 'the property'}</strong>.
              </p>
              <label style={{
                display: 'block', padding: file ? '16px' : '32px 20px',
                background: file ? '#f4fee8' : '#f9fafb',
                border: `2px dashed ${file ? '#acf44e' : '#d1d5db'}`,
                borderRadius: '14px', cursor: 'pointer', textAlign: 'center', marginBottom: '16px',
              }}>
                <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                  onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
                {file ? (
                  <div>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>✅</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#2d6a0a' }}>{file.name}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Tap to change</div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#374151' }}>Upload Disclosure PDF</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>PDF or image accepted</div>
                  </>
                )}
              </label>
              {uploadError && <div style={{ fontSize: '13px', color: '#ff082f', fontWeight: '600', marginBottom: '12px' }}>{uploadError}</div>}
              <button onClick={handleSubmit} disabled={!file || uploading}
                style={{
                  width: '100%', padding: '14px',
                  background: !file || uploading ? '#e5e7eb' : '#acf44e',
                  color: !file || uploading ? '#9ca3af' : '#1a1a1a',
                  border: 'none', borderRadius: '12px',
                  cursor: !file || uploading ? 'not-allowed' : 'pointer',
                  fontWeight: '800', fontSize: '15px',
                }}>
                {uploading ? 'Uploading...' : 'Submit Disclosure'}
              </button>
              <p style={{ margin: '16px 0 0', fontSize: '12px', color: '#9ca3af', textAlign: 'center' }}>
                Questions? <a href="mailto:support@godoorly.com" style={{ color: '#3a6ad9' }}>support@godoorly.com</a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
